import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useAuditLogs } from "../../../hooks/useAuditLogs";
import type { AuditLogEntry } from "../../../server/audit-logs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getAuditLogs } from "../../../server/audit-logs";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/audit-logs/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.auditLogs.list(1, 20),
      queryFn: async () => {
        const result = await getAuditLogs({
          data: { accessToken: session.access_token, page: 1, limit: 20 },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="list" />,
  errorComponent: createRouteError("Audit Logs"),
  component: AuditLogsPage,
});

function AuditLogsPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedAuditLogs accessToken={session.access_token} />;
}

const ACTION_LABELS: Record<string, { label: string; variant: string }> = {
  suspend_user: { label: "Suspend User", variant: "text-destructive" },
  unsuspend_user: { label: "Unsuspend User", variant: "text-green-600" },
  assign_role: { label: "Assign Role", variant: "text-blue-600" },
  remove_role: { label: "Remove Role", variant: "text-orange-600" },
  create_post: { label: "Create Post", variant: "text-muted-foreground" },
  delete_post: { label: "Delete Post", variant: "text-destructive" },
};

function AuthenticatedAuditLogs({ accessToken }: { accessToken: string }) {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch } = useAuditLogs(
    { accessToken },
    { page, limit }
  );

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Audit Logs</h1>
            <p className="text-muted-foreground">
              Track admin actions and system events
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
            <Button variant="outline" asChild>
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              {data ? `${data.total} total entries` : "Loading..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && (
              <p className="text-muted-foreground">Loading audit logs...</p>
            )}
            {error && (
              <p className="text-destructive">Error: {error.message}</p>
            )}
            {!isLoading && !error && (!data || data.logs.length === 0) && (
              <p className="text-muted-foreground">No audit logs found.</p>
            )}
            {!isLoading && !error && data && data.logs.length > 0 && (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Time</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Action</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Entity</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">User</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.logs.map((log: AuditLogEntry) => {
                        const actionInfo = ACTION_LABELS[log.action] ?? {
                          label: log.action,
                          variant: "text-muted-foreground",
                        };
                        return (
                          <tr key={log.id} className="border-b last:border-0">
                            <td className="py-2 px-3 text-muted-foreground">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 font-medium ${actionInfo.variant}`}>
                              {actionInfo.label}
                            </td>
                            <td className="py-2 px-3">
                              {log.entity_type && (
                                <span className="text-muted-foreground">
                                  {log.entity_type}
                                  {log.entity_id && ` #${log.entity_id.slice(0, 8)}`}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                              {log.user_id.slice(0, 8)}...
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
